"use client";

import { useState, useEffect, useRef } from "react";
import clsx from "clsx";

import { SwiperSlide } from "swiper/react";
import Swiper from "@/components/swiper";
import TabSwitcher from "@/components/TabSwitcher";

/**
 * Generic tab + swiper component for "last minute" style sections.
 *
 * Expected props from PageBuilder:
 * - lastMinuteData: {
 *     busLastMinuteSales: [],
 *     flightLastMinuteSales: [],
 *     ferryLastMinuteSales: [],
 *   }
 * - tabsConfig: [
 *     {
 *       id: "bus",
 *       label: "Otobüs",
 *       dataKey: "busLastMinuteSales",
 *       cardComponent: "RouteDealCard",
 *       cardProps: { dateFormatType: "standard" },
 *     },
 *     ...
 *   ]
 * - swiperSettings: {
 *     slideClass: string;
 *     spacingClass?: string;
 *     options?: SwiperOptions;
 *   }
 *
 * SEO için: Tüm tab içerikleri DOM'da render edilir, sadece görünürlük
 * Tailwind sınıfları ile kontrol edilir (show/hide tekniği).
 */

// Bus last minute kartı: mevcut RouteDealCard ile aynı data formatını bekler
import RouteDealCard from "@/components/featured/bus/cards/RouteDealCard";
import FerryLastMinuteCard from "@/components/pages/ferry/cards/RouteCard";
import FlightLastMinuteCard from "@/components/pages/flight/FlightTicketCard";
import OtelLastMinuteCard from "@/components/featured/otel/cards/OtelLastMinuteCard";
import HomePopularRentalCarCard from "@/components/pages/car/sections/popular-rental-cars/HomePopularRentalCarCard";

const INTERNAL_CARD_COMPONENTS = {
  RouteDealCard,
  FlightLastMinuteCard,
  FerryLastMinuteCard,
  OtelLastMinuteCard,
  HomePopularRentalCarCard,
};

// Her card componenti için varsayılan data prop ismi
const DEFAULT_DATA_PROP_BY_COMPONENT = {
  RouteDealCard: "item",
  FerryLastMinuteCard: "item",
  FlightLastMinuteCard: "flight",
  OtelLastMinuteCard: "item",
  HomePopularRentalCarCard: "item",
};

export default function LastMinuteTabbedSwiper({
  lastMinuteData = {},
  tabsConfig = [],
  swiperSettings = {},
}) {
  const safeArray = (value) => (Array.isArray(value) ? value : []);

  // Konfig'de olup datası boş olan tab'ları gizle
  const tabs = (tabsConfig.length > 0 ? tabsConfig : []).filter((tab) => {
    // dataKey verilmemişse tab'ı olduğu gibi göster
    if (!tab.dataKey) return true;
    const items = safeArray(lastMinuteData?.[tab.dataKey]);
    return items.length > 0;
  });

  const [activeTab, setActiveTab] = useState(tabs[0]?.id || null);
  const isInitialMount = useRef(true);
  const viewedTabsRef = useRef(new Set());

  // Tab geçişinde view_recommendation tetikle (ilk tab buildEvent ile zaten tetikleniyor)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      viewedTabsRef.current.add(activeTab);
      return;
    }

    if (!activeTab || viewedTabsRef.current.has(activeTab)) return;
    viewedTabsRef.current.add(activeTab);

    const activeTabConfig = tabs.find((t) => t.id === activeTab);
    if (!activeTabConfig?.dataKey) return;

    const items = safeArray(lastMinuteData?.[activeTabConfig.dataKey]);
    if (items.length === 0 || typeof window === "undefined") return;

    const dataLayer = window.dataLayer;
    if (!Array.isArray(dataLayer)) return;
    items.forEach((item) => {
      const payload = item?.recommendationPayload;
      if (payload) {
        dataLayer.push({
          ...payload,
          event_action: "view_recommendation",
        });
      }
    });
  }, [activeTab, lastMinuteData, tabs]);

  const slideClass = swiperSettings.slideClass;
  const spacingClass = swiperSettings.spacingClass;
  const swiperOptions = {
    slidesPerView: "auto",
    ...(swiperSettings.options || {}),
  };

  // Analytics click handler - select_recommendation dataLayer event
  const onAnalyticsClick = (item) => {
    const payload = item?.recommendationPayload;
    if (payload && typeof window !== "undefined") {
      const dataLayer = window.dataLayer;
      if (!Array.isArray(dataLayer)) return;
      dataLayer.push({
        ...payload,
        event_action: "select_recommendation",
      });
    }
  };

  const renderTabContent = (tab) => {
    const items = safeArray(lastMinuteData?.[tab.dataKey]);
    if (!items.length) return null;

    const CardComponent = INTERNAL_CARD_COMPONENTS[tab.cardComponent];
    // Tanımlı bir Card componenti yoksa bu tab'ı hiç render etme
    if (!CardComponent) return null;
    const cardProps = tab.cardProps || {};

    // Config'den data prop ismini oku, yoksa component tipine göre default kullan
    const dataPropName =
      tab.dataPropName ||
      DEFAULT_DATA_PROP_BY_COMPONENT[tab.cardComponent] ||
      "item";

    return (
      <div
        key={tab.id}
        className={clsx(tab.id === activeTab ? "block" : "hidden")}
        aria-hidden={tab.id !== activeTab}
      >
        <Swiper
          customClass={swiperSettings.customClass}
          swiperOptions={swiperOptions}
        >
          {items.map((item, index) => {
            const key =
              item?.id ||
              item?.RedirectUrl ||
              item?.Url ||
              `${tab.id}-${index}`;

            const dataProp = dataPropName ? { [dataPropName]: item } : {};
            const handleAnalytics = () => onAnalyticsClick(item);

            return (
              <SwiperSlide
                key={`${key}-${index}`}
                className={clsx(slideClass, index > 0 && spacingClass)}
              >
                <CardComponent
                  {...dataProp}
                  {...cardProps}
                  onAnalyticsClick={handleAnalytics}
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    );
  };

  // Hiç data gelen tab yoksa component'i render etme
  if (!tabs.length) {
    return null;
  }

  return (
    <>
      {tabs.length > 1 && (
        <TabSwitcher
          tabs={tabs}
          selected={activeTab}
          onChange={setActiveTab}
          className="mb-8"
        />
      )}

      {/* SEO: tüm tab içerikleri DOM'da, sadece görünürlük class ile kontrol ediliyor */}
      {tabs.map((tab) => renderTabContent(tab))}
    </>
  );
}
